import type { Risk } from '../../types/api';
import type { RiskLocationMatch } from './matchRiskLocationToWorldCity';

interface MatchWorkerRequest {
  id: number;
  risks: Risk[];
}

interface MatchWorkerResponse {
  id: number;
  matches?: RiskLocationMatch[];
  error?: string;
}

let singletonWorker: Worker | null = null;
let requestCounter = 0;

function getWorker(): Worker | null {
  if (typeof Worker === 'undefined') return null;
  if (singletonWorker) return singletonWorker;
  singletonWorker = new Worker(new URL('./matchRiskLocation.worker.ts', import.meta.url), {
    type: 'module',
  });
  return singletonWorker;
}

export async function matchRisksToWorldCitiesInWorker(risks: Risk[]): Promise<RiskLocationMatch[]> {
  const worker = getWorker();
  if (!worker) throw new Error('Worker not available');
  const requestId = ++requestCounter;

  return await new Promise<RiskLocationMatch[]>((resolve, reject) => {
    const onMessage = (event: MessageEvent<MatchWorkerResponse>) => {
      const payload = event.data;
      if (!payload || payload.id !== requestId) return;
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      if (payload.error) {
        reject(new Error(payload.error));
        return;
      }
      resolve(payload.matches ?? []);
    };

    const onError = (event: ErrorEvent) => {
      worker.removeEventListener('message', onMessage);
      worker.removeEventListener('error', onError);
      reject(new Error(event.message || 'Worker execution failed'));
    };

    worker.addEventListener('message', onMessage);
    worker.addEventListener('error', onError);
    const request: MatchWorkerRequest = { id: requestId, risks };
    worker.postMessage(request);
  });
}
